<template>
    <div class="category-filters">
        <span class="category-filters__title --mobile-only">Categories</span>
        <span class="category-filters__title --not-mobile">Category Filters</span>
        <hh-label :for-def="`${this.prefix}-chk-all`"
                  :tabstop="true"
                  class="category-filters__select --all-categories">
            <hh-checkbox :def="`${this.prefix}-chk-all`"
                         :tabstop="false"
                         :indeterminate="checkedFiltersAreMixed"
                         :value="allFiltersAreChecked"
                         class="category-filters__select__checkbox --all-categories"
                         @change="onSelectAllChange" />
            All Categories
        </hh-label>
        <ul class="category-filters__segments">
            <category-filter v-for="segment in segments"
                             :key="segment.key"
                             :prefix="prefix"
                             :segment="segment"
                             :value="segment.show"
                             class="category-filters__segment"
                             @change="onChange" />
        </ul>
    </div>
</template>

<script>

import _ from 'lodash'
import Vuex from 'vuex'

import PlanBuilderManager from '@/plan-builder-manager'

import AnalyticsLogger from '../analytics-logger'
import CategoryFilter from './category-filter'

export default {

    props: {
        prefix: {
            type: String,
            default: 'std',
        },
    },

    mixins: [
        AnalyticsLogger,
    ],

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Array<ImprovementsVuexStateVisibleSegment>}
             */
            visibleSegments: stateStore => stateStore.improvements.visibleSegments,
        }),

        allFiltersAreChecked() {
            return !this.someFiltersAreUnchecked
        },

        checkedFiltersAreMixed() {
            return this.someFiltersAreChecked && this.someFiltersAreUnchecked
        },

        someFiltersAreChecked() {
            return this.visibleSegments.filter(seg => seg.visible).length > 0
        },

        someFiltersAreUnchecked() {
            return this.visibleSegments.filter(seg => !seg.visible).length > 0
        },

        segmentsFromMap() {
            return this.$scheme.segmentKeymap.filter(km => km.key !== 'summary')
        },

        segments() {
            return this.segmentsFromMap.map(seg => {
                const visibilityRef = this.visibleSegments.find(vs => vs.key == seg.key)
                return { key: seg.key, text: seg.text, show: visibilityRef.visible }
            })
        },

        visibleSegmentKeys() {
            return this.visibleSegments.filter(seg => seg.visible).map(seg => seg.key)
        },
    },

    methods: {

        onChange(segment) {
            this._setSegmentVisbility(segment)
            this.trackEvent('filter_measures', `filter_${ segment.text }_${ segment.show ? 'on' : 'off' }`)
        },

        onSelectAllChange(value) {
            if (value)
                this._showAllSegments()
            else
                this._hideAllSegments()
        },

        _setSegmentVisbility(segment) {
            const newSegments = this.visibleSegmentKeys

            if (segment.show && _.includes(newSegments, segment.key) || !segment.show && ! _.includes(newSegments, segment.key))
                return
            else if (segment.show)
                newSegments.push(segment.key)
            else if (!segment.show)
                _.pull(newSegments, segment.key)

            PlanBuilderManager.filterDisplayedImprovementsBySegment(this.$scheme.segmentKeymap, newSegments)
        },

        _showAllSegments() {
            PlanBuilderManager.filterDisplayedImprovementsBySegment(this.$scheme.segmentKeymap, this.visibleSegments.map(seg => seg.key))
            this.trackEvent('filter_measures', 'filter_all_categories_on')
        },

        _hideAllSegments() {
            PlanBuilderManager.filterDisplayedImprovementsBySegment(this.$scheme.segmentKeymap, [])
            this.trackEvent('filter_measures', 'filter_all_categories_off')
        },
    },

    components: {
        CategoryFilter,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\plan-builder\_category-filters.scss

.category-filters {
    font-size: 0.9rem;

    @include element('title') {
        font-weight: bold;
    }

    @include element('select') {
        cursor: pointer;
        display: block;
        margin-top: 0.3rem;
        margin-bottom: 0.2rem;

        @include element('checkbox') {
            cursor: pointer;
        }
    }

    @include element('segments') {
        @include unstyle-list;
        padding-top: 0.1rem;
        border-top: 1px solid;

        > li {
            padding: 0.1rem 0;

            @include bp-min-small {
                padding: 0.3rem 0;
            }
        }
    }
}

</style>
